import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Spinner } from 'reactstrap';
import useAuth from '../../../hooks/useAuth';
import useFilter from '../../../hooks/useFilter';
import useNavBar from '../../../hooks/useNavBar';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import Categories from '../../Categories';
import CourseTypes from '../../CourseTypes';
import IsoCodes from '../../IsoCodes';
import KentuckyCodes from '../../KentuckyCodes';
import WSRBCodes from '../../WSRBCodes';
import TrainingCodes from '../../TrainingCodes';
import DateRange from '../../DateRange';
import Historical from '../../Historical';
import Archived from '../../Archived';

export default function DetailedCompletionsReportForm() {
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const isoCodes = IsoCodes();
    const categories = Categories();
    const courseTypes = CourseTypes();
    const kentuckyCodes = KentuckyCodes();
    const wsrbCodes = WSRBCodes();
    const trainingCodes = TrainingCodes();
    const dateRange = DateRange();
    const historical = Historical();
    const archived = Archived();
    const auth = useAuth();

    const { filter, setFilter } = useFilter();
    const { loading, setLoading } = useNavBar();

    const isKy = auth.auth.isKy;
    const isCa = auth.auth.isCa;
    const isWa = auth.auth.isWa;

    const headers =
        [
            "Full Name",
            "Course/Activity",
            "Session Name",
            "Event Name",
            "Type",
            "Category",
            "Training Hours",
            "Completion Date",
            "Start Time",
            "End Time",
            "ISO Code",
            "Kentucky Code",
            "WSRB Category",
            "CAL-JAC Code",
            "Other Training Codes",
            "Score",
            "Location",
            "Description",
            "Internal Instructors",
            "External Instructors",
            "Department",
            "Personnel ID",
            "Rank",
            "Shift",
            "Station",
            "Division",
            "Battalion",
            "Company",
            "Platoon",
            "Bureau",
            "EMS Certification",
        ];

    if (!isKy) {
        const kyIdx = headers.indexOf('Kentucky Code');
        headers.splice(kyIdx, 1);
    }
    if (!isCa) {
        const caIdx = headers.indexOf('CAL-JAC Code');
        headers.splice(caIdx, 1);
    }
    if (!isWa) {
        const waIdx = headers.indexOf('WSRB Category');
        headers.splice(waIdx, 1);
    }

    useEffect(() => {
        dateRange.setHelpText("Date search is based on the ending date and time of the event");

        if (!isoCodes.loading && !kentuckyCodes.loading && !wsrbCodes.loading && !trainingCodes.loading && !dateRange.loading && !historical.loading && !archived.loading) {
            setLoading(false);
        }

        let adjustedHeaders = [
            ...headers,
            ...(archived.includeArchivedCompletions ? ["Enrollment Status", "Archived Date"] : []) // end of all fields
        ];

        setFilter({
            users: [auth.auth.user],
            groups: [],
            branches: [],
            courseTypes: courseTypes.selectedTypes.map(type => type.value),
            categories: categories.selectedCategories,
            selectAllCategories: categories.selectAll,
            isoCodes: isoCodes.selected.map(code => code.value),
            kentuckyCodes: kentuckyCodes.selected.map(code => code.value),
            wsrbCodes: wsrbCodes.selected.map(code => code.value),
            trainingCodes: trainingCodes.selectedTrainingCodes.map(trainingCode => trainingCode.label),
            trainingCodeTypes: trainingCodes.selectedTrainingCodeTypes.map(trainingCodeType => trainingCodeType.label),
            headers: adjustedHeaders,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            includeHistoricalCompletions: historical.includeHistoricalCompletions,
            hasArchivedCompletions: archived.hasArchivedCompletions,
            includeArchivedCompletions: archived.includeArchivedCompletions,
            includeNullTrainingCodes: trainingCodes.includeNullTrainingCodes,
            includeNullIsoCodes: isoCodes.includeNullIsoCodes,
            includeNullKentuckyCodes: kentuckyCodes.includeNullKentuckyCodes,
            includeNullWSRBCodes: wsrbCodes.includeNullWSRBCodes,
            selectAllTrainingCodes: trainingCodes.selectAll,

            selectAllCourseTypes: courseTypes.selectAll,
            formCourseTypes: courseTypes.courseTypes,
            formCategories: categories.categories,
            formCategoryIds: categories.categoryIds,
            formExpandedCategories: categories.expandedCategories,
            formCodes: isoCodes.isoCodes,
            formKyCodes: kentuckyCodes.kentuckyCodes,
            formWSRBCodes: wsrbCodes.wsrbCodes,
            formTrainingCodes: trainingCodes.trainingCodes,
            formTrainingCodeTypes: trainingCodes.trainingCodeTypes
        });
    }, [
        courseTypes.selectAll,
        courseTypes.courseTypes,
        courseTypes.selectedTypes,
        categories.selectAll,
        categories.categories,
        categories.selectedCategories,
        categories.categoryIds,
        categories.expandedCategories,
        isoCodes.loading,
        isoCodes.selected,
        isoCodes.isoCodes,
        isoCodes.includeNullIsoCodes,
        kentuckyCodes.loading,
        kentuckyCodes.selected,
        kentuckyCodes.kentuckyCodes,
        kentuckyCodes.includeNullKentuckyCodes,
        wsrbCodes.loading,
        wsrbCodes.selected,
        wsrbCodes.wsrbCodes,
        wsrbCodes.includeNullWSRBCodes,
        trainingCodes.loading,
        trainingCodes.selectAll,
        trainingCodes.includeNullTrainingCodes,
        trainingCodes.selectedTrainingCodes,
        trainingCodes.trainingCodes,
        trainingCodes.selectedTrainingCodeTypes,
        trainingCodes.trainingCodeTypes,
        dateRange.loading,
        dateRange.startDate,
        dateRange.endDate,
        historical.includeHistoricalCompletions,
        archived.includeArchivedCompletions,
        archived.hasArchivedCompletions
    ]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const sendfilter = (
            (
                { users, groups, branches, courseTypes, categories, selectAllCategories, isoCodes, kentuckyCodes, wsrbCodes, selectAllTrainingCodes, trainingCodes, trainingCodeTypes, headers, startDate, endDate, includeHistoricalCompletions, includeArchivedCompletions, hasArchivedCompletions, includeNullTrainingCodes, includeNullIsoCodes, includeNullKentuckyCodes, includeNullWSRBCodes }) =>
                ({ users, groups, branches, courseTypes, categories, selectAllCategories, isoCodes, kentuckyCodes, wsrbCodes, selectAllTrainingCodes, trainingCodes, trainingCodeTypes, headers, startDate, endDate, includeHistoricalCompletions, includeArchivedCompletions, hasArchivedCompletions, includeNullTrainingCodes, includeNullIsoCodes, includeNullKentuckyCodes, includeNullWSRBCodes })
        )(filter);

        try {
            await axiosPrivate.post('api/myreports/mydetailedcompletions', sendfilter);
            navigate('/myreports/reports');
        }
        catch (err) {
            console.log(err);
        }
    }

    return (
        loading ? <Spinner variant="primary" animation="border" role="status" className='custom-spinner' /> :
            <Container>
                <Col>
                    <FormGroup>
                        <Card>
                            <CardBody>
                                <FormGroup>
                                    {courseTypes.render}
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </FormGroup>
                    <FormGroup>
                        <Card>
                            <CardBody>
                                <FormGroup>
                                    {categories.render}
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </FormGroup>
                    <FormGroup>
                        <Card>
                            <CardBody>
                                <FormGroup>
                                    {isoCodes.render}
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </FormGroup>
                    {
                        isKy ?
                            <FormGroup>
                                <Card>
                                    <CardBody>
                                        <FormGroup>
                                            {kentuckyCodes.render}
                                        </FormGroup>
                                    </CardBody>
                                </Card>
                            </FormGroup>
                            :
                            null
                    }
                    {
                        isWa ?
                            <FormGroup>
                                <Card>
                                    <CardBody>
                                        <FormGroup>
                                            {wsrbCodes.render}
                                        </FormGroup>
                                    </CardBody>
                                </Card>
                            </FormGroup>
                            :
                            null
                    }
                    <FormGroup>
                        <Card>
                            <CardBody>
                                <FormGroup>
                                    {trainingCodes.render}
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </FormGroup>
                    <FormGroup>
                        <Card>
                            <CardBody>
                                <FormGroup>
                                    {dateRange.render}
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </FormGroup>
                    {
                        historical.hasHistoricalCompletions ?
                            <FormGroup>
                                <Card>
                                    <CardBody>
                                        <FormGroup>
                                            {historical.render}
                                        </FormGroup>
                                    </CardBody>
                                </Card>
                            </FormGroup>
                            :
                            null
                    }
                    {
                        archived.hasArchivedCompletions ?
                            <FormGroup>
                                <Card>
                                    <CardBody>
                                        <FormGroup>
                                            {archived.render}
                                        </FormGroup>
                                    </CardBody>
                                </Card>
                            </FormGroup>
                            :
                            null
                    }
                </Col>
                <footer>
                    <Form onSubmit={handleSubmit}>
                        <Button color="primary">Generate Report</Button>
                        <Link to="/myreports/">
                            <Button className='btn-danger'>Back</Button>
                        </Link>
                    </Form>
                </footer>
            </Container>
    );
}